import React, { Fragment, useState } from "react"

import { useTranslation } from "react-i18next"

import CheckIcon from "@mui/icons-material/Check"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import PolylineIcon from "@mui/icons-material/Polyline"
import { Card, Grid, IconButton } from "@mui/material"
import { Button, Menu, MenuItem, Skeleton, Tag, Typography } from "@synapse-analytics/synapse-ui"

import { getTheme } from "../../hooks/UseTheme"
import { type ScriptCardProps } from "./Interfaces"
import { MarketPlaceIntegrationDialog } from "./components/MarketPlaceIntegrationDialog"

import styles from "./MarketPlace.module.scss"

/**
 * Renders a template card component with details about a specific template.
 *
 * @param {Script} template template data.
 * @returns The `ReactElement` that represents the template card.
 */
export function TemplateCard({ template, MenuOptions, canShowDetails }: Readonly<ScriptCardProps>): React.ReactElement {
  const { name, short_description, isInUse, uuid, script_uuid, logo, tags } = template
  const { t } = useTranslation("marketplace", { keyPrefix: "templateCard" })

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)

  const [isBuiltinScriptDialogOpen, setIsBuiltinScriptDialogOpen] = useState<{ isOpen: boolean; isEditMode: boolean }>({
    isOpen: false,
    isEditMode: false,
  })

  const theme = getTheme()

  return (
    <Fragment>
      {isBuiltinScriptDialogOpen.isOpen && (
        <MarketPlaceIntegrationDialog
          isOpen={isBuiltinScriptDialogOpen.isOpen}
          renderView="scripts"
          scriptInfo={{ name: name, uuid: uuid, scriptId: script_uuid as string, logo: logo as string }}
          isNewScriptTriggered={!isInUse}
          onClose={() => setIsBuiltinScriptDialogOpen({ isOpen: false, isEditMode: false })}
          openInEditMode={isBuiltinScriptDialogOpen.isEditMode}
          canShowDetails={canShowDetails}
        />
      )}

      <Card className="card-box-shadow" style={{ minHeight: "100%", height: "100%" }}>
        <Grid container direction="column" className={styles.card} gap={1.5} flex={1} minHeight={"100%"}>
          <Grid item xs={12} display="flex" wrap="nowrap" justifyContent="space-between" alignItems="flex-start">
            {logo ? (
              <img src={logo} alt={name} style={{ borderRadius: "4px", height: "36px", width: "36px" }} />
            ) : (
              <PolylineIcon sx={{ color: theme.palette.base.text[1], width: "36px", height: "36px" }} />
            )}

            {/* Menu  */}
            {MenuOptions ? (
              <Grid item xs={2} container justifyContent="flex-end" alignItems="flex-start">
                <IconButton
                  style={{ marginTop: "-5px" }}
                  onClick={(e) => setMenuAnchor(e?.currentTarget)}
                  aria-label="settings"
                  size="small"
                >
                  <MoreHorizIcon sx={{ color: "var(--base-text-2)" }} />
                </IconButton>

                <Menu
                  id="template-card-menu"
                  onClose={() => setMenuAnchor(null)}
                  anchorEl={menuAnchor}
                  menuMaxContent
                  open={Boolean(menuAnchor)}
                >
                  {MenuOptions?.map((option) => (
                    <MenuItem
                      onClick={() => {
                        option.onClick()
                        setMenuAnchor(null)
                      }}
                      key={option.title}
                    >
                      <Typography variant="a" color={option.variant ?? "gray"} variantColor={2}>
                        {option.title}
                      </Typography>
                    </MenuItem>
                  ))}
                </Menu>
              </Grid>
            ) : (
              canShowDetails && (
                <Grid item xs={2} container justifyContent="flex-end" alignSelf="center">
                  <Button
                    variant="primary"
                    size="small"
                    onClick={() => setIsBuiltinScriptDialogOpen({ isOpen: true, isEditMode: true })}
                  >
                    {t("connect")}
                  </Button>
                </Grid>
              )
            )}
          </Grid>

          {/* Name */}
          <Grid container item xs={12} gap={1} display={"flex"} flexWrap="nowrap" alignItems="center">
            <Typography
              variant="h2-bold"
              noWrap
              onClick={() => setIsBuiltinScriptDialogOpen({ isOpen: true, isEditMode: false })}
              className={styles.scriptName}
              color="important"
              variantColor={2}
            >
              {name}
            </Typography>

            {isInUse && (
              <Tag size="small" color="green">
                <CheckIcon sx={{ fontSize: "16px", marginRight: "4px" }} />
                {t("connected")}
              </Tag>
            )}
          </Grid>

          {Boolean(tags?.length && tags?.length >= 1) && (
            <Grid item gap={0.5} display={"flex"}>
              {tags?.map((tag) => (
                <Tag size="small" color="blue" key={tag.key}>
                  {tag.value}
                </Tag>
              ))}
            </Grid>
          )}

          <Grid item xs={12} justifyContent="flex-start">
            <Typography variant="p">{short_description}</Typography>
          </Grid>
        </Grid>
      </Card>
    </Fragment>
  )
}

/**
 * Loading card for script card
 * @returns {React.ReactElement}
 */
export function TemplateLoadingCard(): React.ReactElement {
  return (
    <Card className={"card-box-shadow"}>
      <Grid container direction="column" className={styles.card}>
        <Grid item xs={3} mb={0.5}>
          <Skeleton variant="rectangular" height={19} width={"20%"} />
        </Grid>

        <Grid item mt={1} xs={12}>
          <Skeleton variant="rectangular" height={19} width={"60%"} />
        </Grid>

        <Grid item xs={12} container flexWrap="nowrap" spacing={1} alignItems="center">
          <Grid item mt={1}>
            <Skeleton variant="circular" width={32} height={32} />
          </Grid>

          <Grid direction="column" container item mt={1}>
            <Grid item xs={6} my={1}>
              <Skeleton variant="rectangular" height={10} width={"80%"} />
            </Grid>
            <Grid item xs={3} mb={0.5}>
              <Skeleton variant="rectangular" height={10} width={"30%"} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Card>
  )
}
